'use client'
import React, { useState } from 'react'
import Input from '../Input'
import Button from '../Button'
import Contact from '../Contact'
import { EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/solid'
import Modal from '../Modal'
import Calendar from '../Calendar'
import TimeSlotPicker from '../TimeSlotPicker'
import useCalendar from '../Calendar/useCalendar'
import ApiClient from '@/services/ApiClient'
import { DateTime } from 'luxon'
import { validateFormData, validateName, validateEmail, validatePhone } from '@/utils/formValidation'

const GetInTouch = () => {
    // Get slots data from calendar hook
    const { slotsMap } = useCalendar()

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
        date: '',
        timeslotId: null
    })
    const [fieldErrors, setFieldErrors] = useState({
        name: null,
        email: null,
        phone: null
    })
    const [touchedFields, setTouchedFields] = useState({
        name: false,
        email: false,
        phone: false
    })
    const [isModalDateOpen, setIsModalDateOpen] = useState(false)
    const [currentStep, setCurrentStep] = useState(1) // 1, 2, or 3
    const [selectedDate, setSelectedDate] = useState(null)
    const [selectedSlot, setSelectedSlot] = useState(null)

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({ ...prev, [name]: null }))
        }
    }

    const handleFieldBlur = (e) => {
        const { name } = e.target
        setTouchedFields(prev => ({ ...prev, [name]: true }))
        const error = validateField(name, formData[name])
        setFieldErrors(prev => ({ ...prev, [name]: error }))
    }

    const validateField = (fieldName, value) => {
        switch (fieldName) {
            case 'name':
                return validateName(value)
            case 'email':
                return validateEmail(value)
            case 'phone':
                return validatePhone(value)
            default:
                return null
        }
    }

    const handleDateSelect = (date) => {
        setSelectedDate(date)
        setSelectedSlot(null) // Reset slot when date changes
        setFormData(prev => ({ ...prev, date, timeslotId: null }))
    }

    const handleSlotSelect = (slot) => {
        setSelectedSlot(slot)
        setFormData(prev => ({ ...prev, timeslotId: slot.id }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        // Validate all fields before opening modal
        const errors = validateFormData(formData)
        if (Object.values(errors).some(error => error !== null)) {
            setFieldErrors(errors)
            setTouchedFields({
                name: true,
                email: true,
                phone: true
            })
            // Optional: toast notification if available
            // notify?.error?.('Please fix the errors in your form before proceeding.')
            return
        }

        setIsModalDateOpen(true)
    }

    const handleCancel = () => {
        setIsModalDateOpen(false)
        setCurrentStep(1)
        setSelectedDate(null)
        setSelectedSlot(null)
    }

    const handleConfirmBooking = async () => {
        try {
            const apiClient = ApiClient.getInstance()

            await apiClient.post('/bookings/', {
                timeslot_id: formData.timeslotId,
                name: formData.name,
                email: formData.email,
                phone_number: formData.phone,
                message: formData.message || ''
            }, {
                notification: {
                    showNotification: true,
                    successMessage: 'Booking submitted! Check your email for confirmation.',
                    errorMessage: 'Failed to submit booking. Please try again.'
                }
            })

            // Success - close modal and reset
            setIsModalDateOpen(false)
            setCurrentStep(1)
            setSelectedDate(null)
            setSelectedSlot(null)
            setFormData({
                name: '',
                email: '',
                phone: '',
                message: '',
                date: '',
                timeslotId: null
            })
            setFieldErrors({
                name: null,
                email: null,
                phone: null
            })
            setTouchedFields({
                name: false,
                email: false,
                phone: false
            })

        } catch (error) {
            console.error('Booking failed:', error)
            // Error notification already shown by ApiClient
        }
    }

    // Step Indicator Component
    const StepIndicator = ({ currentStep }) => {
        const steps = [
            { number: 1, label: 'Select Date' },
            { number: 2, label: 'Select Time' },
            { number: 3, label: 'Confirm' }
        ]

        return (
            <div className="flex items-center justify-between mb-6 max-sm:mb-4">
                {steps.map((step, index) => (
                    <div key={step.number} className="flex items-center flex-1">
                        {/* Step circle */}
                        <div className={`
                            w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                            ${currentStep === step.number ? 'bg-secondary text-black' : ''}
                            ${currentStep > step.number ? 'bg-primary text-white' : ''}
                            ${currentStep < step.number ? 'bg-gray-200 text-gray-500' : ''}
                        `}>
                            {step.number}
                        </div>

                        {/* Step label */}
                        <span className={`ml-2 text-sm max-sm:text-xs ${currentStep === step.number ? 'font-bold text-primary' : 'text-gray-600'}`}>
                            {step.label}
                        </span>

                        {/* Connector line (except for last step) */}
                        {index < steps.length - 1 && (
                            <div className="flex-1 h-0.5 bg-gray-200 mx-4 max-sm:mx-2" />
                        )}
                    </div>
                ))}
            </div>
        )
    }

    // Render step content based on currentStep
    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div>
                        <h3 className="text-lg font-semibold text-primary mb-4">
                            Select a Date
                        </h3>
                        <Calendar
                            onDateSelect={handleDateSelect}
                            selectedDate={selectedDate}
                        />
                    </div>
                )

            case 2:
                return (
                    <div>
                        <h3 className="text-lg font-semibold text-primary mb-4">
                            Select a Time Slot
                        </h3>
                        <TimeSlotPicker
                            selectedDate={selectedDate}
                            slots={slotsMap.get(selectedDate) || []}
                            onSlotSelect={handleSlotSelect}
                            selectedSlotId={selectedSlot?.id}
                        />
                    </div>
                )

            case 3:
                return (
                    <div>
                        <h3 className="text-lg font-semibold text-primary mb-4">
                            Confirm Your Booking
                        </h3>

                        <div className="space-y-4">
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <h4 className="font-medium text-primary mb-2">Personal Information</h4>
                                <div className="text-sm text-gray-700 space-y-1">
                                    <p>Name: {formData.name}</p>
                                    <p>Email: {formData.email}</p>
                                    <p>Phone: {formData.phone}</p>
                                </div>
                            </div>

                            <div className="p-4 bg-secondary/10 rounded-lg border border-secondary/30">
                                <h4 className="font-medium text-primary mb-2">Appointment Details</h4>
                                <div className="text-sm text-gray-700 space-y-1">
                                    <p>Date: {DateTime.fromISO(selectedDate).toFormat('MMMM d, yyyy')}</p>
                                    <p>Time: {selectedSlot.start} - {selectedSlot.end} PT</p>
                                </div>
                            </div>

                            {formData.message && (
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <h4 className="font-medium text-primary mb-2">Message</h4>
                                    <p className="text-sm text-gray-700">{formData.message}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )

            default:
                return null
        }
    }

    // Render navigation buttons based on currentStep
    const renderNavigationButtons = () => {
        const isStep1Valid = selectedDate !== null
        const isStep2Valid = selectedSlot !== null

        return (
            <div className="flex gap-3 mt-6">
                {/* Back button (show on steps 2 and 3) */}
                {currentStep > 1 && (
                    <Button
                        onClick={() => setCurrentStep(currentStep - 1)}
                        variant="outline-secondary"
                        className="flex-1"
                    >
                        ← Back
                    </Button>
                )}

                {/* Cancel button (show on step 1) */}
                {currentStep === 1 && (
                    <Button
                        onClick={handleCancel}
                        variant="outline-secondary"
                        className="flex-1"
                    >
                        Cancel
                    </Button>
                )}

                {/* Next button (steps 1 and 2) */}
                {currentStep < 3 && (
                    <Button
                        onClick={() => setCurrentStep(currentStep + 1)}
                        variant="secondary"
                        className="flex-1"
                        disabled={
                            (currentStep === 1 && !isStep1Valid) ||
                            (currentStep === 2 && !isStep2Valid)
                        }
                    >
                        Next →
                    </Button>
                )}

                {/* Confirm button (step 3) */}
                {currentStep === 3 && (
                    <Button
                        onClick={handleConfirmBooking}
                        variant="secondary"
                        className="flex-1"
                    >
                        Confirm Booking
                    </Button>
                )}
            </div>
        )
    }

  return (
    <div id='contact' className='w-full bg-gray-50 flex flex-col items-center justify-center gap-10 py-20 px-10 max-xs:px-5 max-xs:py-10'>
        <div className='w-full flex flex-col gap-6 items-center justify-center'>
            <div className='flex flex-col w-full items-center gap-4'>
                <h3 className='text-5xl font-bold text-primary'>Get In Touch</h3>
                <span className='w-1/12 rounded-full h-1 bg-secondary'></span>
            </div>
            <p className='text-primary'>We&apos;re here to help you achieve your financial goals. Contact us today to schedule a consultation.</p>
        </div>
        <div className='w-full flex items-center justify-center h-full gap-10 max-md:flex-col'>
            <form onSubmit={handleSubmit} className='flex flex-col gap-4 w-1/2 max-md:w-full'>
                <Input name='name' type='text' placeholder='Name' label='Name' id='name' value={formData.name} onChange={handleChange} onBlur={handleFieldBlur} error={touchedFields.name && fieldErrors.name !== null} errorMessage={fieldErrors.name} required={true} />
                <Input name='email' type='email' placeholder='Email' label='Email' id='email' value={formData.email} onChange={handleChange} onBlur={handleFieldBlur} error={touchedFields.email && fieldErrors.email !== null} errorMessage={fieldErrors.email} required={true} />
                <Input name='phone' type='tel' placeholder='Phone' label='Phone' id='phone' value={formData.phone} onChange={handleChange} onBlur={handleFieldBlur} error={touchedFields.phone && fieldErrors.phone !== null} errorMessage={fieldErrors.phone} required={false} />
                <Input name='message' type='textarea' placeholder='Message' label='Message' id='message' value={formData.message} onChange={handleChange} />
                <Button onClick={handleSubmit} className='w-full' variant='secondary' type='submit'>Book Consultation</Button>
                <Modal
                    isOpen={isModalDateOpen}
                    onClose={handleCancel}
                    title="Book Your Consultation"
                    maxWidth="max-w-2xl"
                >
                    <div className="p-6 max-sm:p-4">
                        {/* Step Indicator */}
                        <StepIndicator currentStep={currentStep} />

                        {/* Step Content */}
                        <div className="mb-6">
                            {renderStepContent()}
                        </div>

                        {/* Navigation Buttons - Sticky on mobile for better UX */}
                        <div className="sticky bottom-0 bg-white pt-4 -mx-6 px-6 max-sm:-mx-4 max-sm:px-4 pb-2 border-t border-gray-100">
                            {renderNavigationButtons()}
                        </div>
                    </div>
                </Modal>
            </form>
            <div className='w-1/2 h-full flex flex-col gap-4 max-md:w-full'>
                <div className='flex flex-col gap-4 shadow-[0_2px_6px_4px_rgba(0,0,0,0.1)] px-4 py-8 rounded-lg'>
                    <h5 className='text-xl font-montserrat font-bold text-primary'>Contact Information</h5>
                    <div className='flex flex-col gap-4'>
                        <Contact title='Email' contactInfo='rlal@planwithlal.com' icon={<EnvelopeIcon />} />
                        <Contact title='Phone' contactInfo='916.560.1244' icon={<PhoneIcon />} />
                    </div>
                </div>
                <div className='flex flex-col gap-4 shadow-[0_2px_6px_4px_rgba(0,0,0,0.1)] rounded-lg bg-primary px-4 py-10'>
                    <h5 className='text-xl font-montserrat font-bold text-white'>Follow Us</h5>
                    <div className='flex items-center gap-4'>
                        <a className='bg-secondary cursor-pointer w-10 h-10 rounded-full flex items-center justify-center' href='https://www.instagram.com/legacyassetleaders' target='_blank' rel='noopener noreferrer'>
                            <svg className='w-6 h-6' xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="50" height="50" viewBox="0 0 50 50">
                                <path d="M 16 3 C 8.83 3 3 8.83 3 16 L 3 34 C 3 41.17 8.83 47 16 47 L 34 47 C 41.17 47 47 41.17 47 34 L 47 16 C 47 8.83 41.17 3 34 3 L 16 3 z M 37 11 C 38.1 11 39 11.9 39 13 C 39 14.1 38.1 15 37 15 C 35.9 15 35 14.1 35 13 C 35 11.9 35.9 11 37 11 z M 25 14 C 31.07 14 36 18.93 36 25 C 36 31.07 31.07 36 25 36 C 18.93 36 14 31.07 14 25 C 14 18.93 18.93 14 25 14 z M 25 16 C 20.04 16 16 20.04 16 25 C 16 29.96 20.04 34 25 34 C 29.96 34 34 29.96 34 25 C 34 20.04 29.96 16 25 16 z"></path>
                            </svg>
                        </a>
                        <a className='bg-secondary cursor-pointer w-10 h-10 rounded-full flex items-center justify-center' href='https://www.youtube.com/legacyassetleaders' target='_blank' rel='noopener noreferrer'>
                            <svg className='w-6 h-6' xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="50" height="50" viewBox="0 0 50 50">
                                <path d="M 44.898438 14.5 C 44.5 12.300781 42.601563 10.699219 40.398438 10.199219 C 37.101563 9.5 31 9 24.398438 9 C 17.800781 9 11.601563 9.5 8.300781 10.199219 C 6.101563 10.699219 4.199219 12.199219 3.800781 14.5 C 3.398438 17 3 20.5 3 25 C 3 29.5 3.398438 33 3.898438 35.5 C 4.300781 37.699219 6.199219 39.300781 8.398438 39.800781 C 11.898438 40.5 17.898438 41 24.5 41 C 31.101563 41 37.101563 40.5 40.601563 39.800781 C 42.800781 39.300781 44.699219 37.800781 45.101563 35.5 C 45.5 33 46 29.398438 46.101563 25 C 45.898438 20.5 45.398438 17 44.898438 14.5 Z M 19 32 L 19 18 L 31.199219 25 Z"></path>
                            </svg>
                        </a>
                        <a className='bg-secondary cursor-pointer w-10 h-10 rounded-full flex items-center justify-center' href='https://www.tiktok.com/@legacyassetleaders' target='_blank' rel='noopener noreferrer'>
                            <svg className='w-6 h-6 ' xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="50" height="50" viewBox="0 0 50 50">
                                <path d="M41,4H9C6.243,4,4,6.243,4,9v32c0,2.757,2.243,5,5,5h32c2.757,0,5-2.243,5-5V9C46,6.243,43.757,4,41,4z M37.006,22.323 c-0.227,0.021-0.457,0.035-0.69,0.035c-2.623,0-4.928-1.349-6.269-3.388c0,5.349,0,11.435,0,11.537c0,4.709-3.818,8.527-8.527,8.527 s-8.527-3.818-8.527-8.527s3.818-8.527,8.527-8.527c0.178,0,0.352,0.016,0.527,0.027v4.202c-0.175-0.021-0.347-0.053-0.527-0.053 c-2.404,0-4.352,1.948-4.352,4.352s1.948,4.352,4.352,4.352s4.527-1.894,4.527-4.298c0-0.095,0.042-19.594,0.042-19.594h4.016 c0.378,3.591,3.277,6.425,6.901,6.685V22.323z"></path>
                            </svg>
                        </a>
                        <a className='bg-secondary cursor-pointer w-10 h-10 rounded-full flex items-center justify-center' href='https://www.linkedin.com/company/legacy-asset-leaders' target='_blank' rel='noopener noreferrer'>
                            <svg className='w-6 h-6' xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="50" height="50" viewBox="0 0 50 50">
                                <path d="M41,4H9C6.24,4,4,6.24,4,9v32c0,2.76,2.24,5,5,5h32c2.76,0,5-2.24,5-5V9C46,6.24,43.76,4,41,4z M17,20v19h-6V20H17z M11,14.47c0-1.4,1.2-2.47,3-2.47s2.93,1.07,3,2.47c0,1.4-1.12,2.53-3,2.53C12.2,17,11,15.87,11,14.47z M39,39h-6c0,0,0-9.26,0-10 c0-2-1-4-3.5-4.04h-0.08C27,24.96,26,27.02,26,29c0,0.91,0,10,0,10h-6V20h6v2.56c0,0,1.93-2.56,5.81-2.56 c3.97,0,7.19,2.73,7.19,8.26V39z"></path>
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
  )
}

export default GetInTouch